/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  FC,
  Fragment,
  useEffect,
  MouseEvent,
  useRef,
  useState,
  useMemo,
  PropsWithChildren,
} from "react";

import { Link, PageProps, navigate } from "gatsby";

import classNames from "classnames";

import Icon from "@components/common/Icon";
import Button from "@components/common/Button";
import Banner from "@components/common/Banner";
import ProductDropDownMenu from "@components/common/ProductDropDownMenu";

import { selectCartItems, toggleCart } from "@store/cart";
import { useDispatch, useSelector } from "react-redux";
import { gaViewCart } from "@analytics/google";
import getItems from "@helpers/items/getItems";
import TextSwitcher from "@components/common/text-switcher/TextSwitcher";

const Wrapper = ({ children }: PropsWithChildren) => (
  <div style={{ marginBottom: 4 }}>{children}</div>
);

const Header: FC<{ location: PageProps["location"] }> = ({ location }) => {
  const body = useRef<HTMLElement>();
  const searchRef = useRef<HTMLInputElement>(null);
  const items = useMemo(() => getItems(), []);

  const [isActive, setIsActive] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredItems, setFilteredItems] = useState(items);

  const cartItems = useSelector(selectCartItems);
  const dispatch = useDispatch();

  const handleToggleCart = () => {
    dispatch(toggleCart());
  };

  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const texts = useMemo(() => {
    return [
      {
        paragraph: (
          <Wrapper>
            <span className="black-reviews__text">
              100,000+ 5 Star Medipups Reviews ⭐️⭐️⭐️⭐️⭐️
            </span>
          </Wrapper>
        ),
      },
      {
        paragraph: (
          <Wrapper>
            <span className="black-reviews__text manufactured">
              Made In The USA from domestic & globally sourced ingredients
            </span>
          </Wrapper>
        ),
      },
      {
        paragraph: (
          <Wrapper>
            <span className="black-reviews__text">Free Shipping + 60 day money-back guarantee</span>
          </Wrapper>
        ),
      },
    ];
  }, []);

  const handleCartClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    handleToggleCart();
    gaViewCart(cartItems);
  };

  const onMouseLeave = (): void => {
    setIsActive(false);
  };

  const toggleMenu = (
    e: React.MouseEvent<HTMLDivElement | HTMLButtonElement | HTMLAnchorElement>
  ): void => {
    e.stopPropagation();

    if (isMobile) {
      setIsOpen((prevIsOpen) => {
        if (prevIsOpen) {
          body?.current?.classList.remove("modal-open");

          return !prevIsOpen;
        }

        body?.current?.classList.add("modal-open");

        return !prevIsOpen;
      });
    }
  };

  useEffect(() => {
    if (searchOpen) {
      body?.current?.classList.add("modal-open");
    } else {
      body?.current?.classList.remove("modal-open");
    }

    return () => {
      body?.current?.classList.remove("modal-open");
    };
  }, [searchOpen]);

  const handleLogoClick = (): void => {
    setIsOpen(false);
    body?.current?.classList.remove("modal-open");
  };

  useEffect(() => {
    body.current = document.body;
    body.current.classList.remove("modal-open");

    if (window?.innerWidth < 1023) {
      setIsMobile(true);
    }

    const handleResize = (): void => {
      if (window?.innerWidth > 1023) {
        body?.current?.classList.remove("modal-open");
        setIsOpen(false);
        setIsMobile(false);
      } else {
        setIsMobile(true);
      }
    };

    window?.addEventListener("resize", handleResize);

    return () => {
      body?.current?.classList.remove("modal-open");
      window?.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const filtered = items.filter((item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    setFilteredItems(filtered);
  }, [searchQuery, items]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (searchOpen && searchRef.current && !searchRef.current.parentElement?.contains(target)) {
        setSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchOpen]);

  return (
    <Fragment>
      <header
        className={classNames("header-container", { open: isOpen })}
        onMouseLeave={onMouseLeave}
      >
        <nav className="header">
          <div className="nav-btn-container">
            <button
              className="nav-btn burger-button"
              type="button"
              onClick={toggleMenu}
              aria-label="burger-menu"
            >
              <Icon name="burger" />
            </button>
            <Button
              className="search-button --mobile"
              color="link"
              icon="search"
              onClick={() => {
                setSearchOpen((prev) => !prev);
                setSearchQuery("");
                setTimeout(() => searchRef.current?.focus(), 100);
              }}
              aria-label="search"
            />
          </div>
          <div className="items">
            <ProductDropDownMenu
              toggleMenu={toggleMenu}
              isActive={isActive}
              setIsActive={setIsActive}
              location={location}
            />
            <Link
              to="/about-us"
              className="nav-link"
              onClick={toggleMenu}
              onMouseEnter={onMouseLeave}
            >
              About Us
            </Link>
            <Link to="/faqs" className="nav-link" onClick={toggleMenu} onMouseEnter={onMouseLeave}>
              FAQS
            </Link>
            <Link to="/blog" className="nav-link" onClick={toggleMenu} onMouseEnter={onMouseLeave}>
              Learn
            </Link>
            <Button
              className="cta-button mobile"
              color="yellow"
              icon="gift"
              onClick={() => navigate(["/box/"].includes(location.pathname) ? "/wizard" : "/box")}
            >
              Get FREE Gift
            </Button>
          </div>
          <Link
            to={location.pathname.includes("box") ? "/box" : "/"}
            className="logo"
            onClick={handleLogoClick}
            aria-label="Medipups"
            onMouseEnter={onMouseLeave}
          >
            <Icon name="logo" width={180} height={37} />
          </Link>
          <div className="buttons">
            <Button
              className="search-button"
              color="link"
              icon="search"
              onClick={() => {
                setSearchOpen((prev) => !prev);
                setSearchQuery("");
                setTimeout(() => searchRef.current?.focus(), 100);
              }}
              aria-label="search"
            />

            {searchOpen && (
              <React.Fragment>
                <div className="search-overlay" />
                <div className="search-dropdown">
                  <input
                    ref={searchRef}
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <div className="search-results">
                    {filteredItems.slice(0, 4).map((item) => (
                      <Link
                        key={item.id}
                        to={`/shop/${item?.information?.slug}`}
                        className="search-item"
                        onClick={() => setSearchOpen(false)}
                      >
                        <img src={item.information?.images.cartImg} alt={item.name} />
                        <span>{item.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </React.Fragment>
            )}

            <Button
              className="profile-button"
              color="link"
              icon="account"
              onClick={() => navigate("/login")}
              aria-label="account"
              onMouseEnter={onMouseLeave}
              title="Account"
            />
            <Button
              className="cart-button"
              color="link"
              icon="cart"
              onClick={handleCartClick}
              aria-label="cart"
              onMouseEnter={onMouseLeave}
            >
              {cartItems.length > 0 && (
                <div className="cart-button-counter">{cartItems.length}</div>
              )}
            </Button>
            <Button
              className="cta-button"
              color="yellow"
              icon="gift"
              onClick={() => navigate(["/box/"].includes(location.pathname) ? "/wizard" : "/box")}
            >
              Get FREE Gift
            </Button>
          </div>
        </nav>
      </header>

      <div className="header-overlay" onClick={toggleMenu} />
    </Fragment>
  );
};

export default Header;
